import { AuthUser } from 'types/user';
import { hasUserSubscriptionAccess } from 'utils/subscription';

const AUTH_TOKEN_KEY = 'auth_token';
const SUBSCRIPTION_ACTIVE_KEY = 'subscription_active';
const AUTH_USER_KEY = 'auth_user';
const LIBRARY_ACCESS_COOKIE = 'learnsocialai_library_access';

const dispatchAuthChange = () => window.dispatchEvent(new Event('auth-change'));
const dispatchSubscriptionChange = () => window.dispatchEvent(new Event('subscription-change'));

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, days = 30) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function removeCookie(name: string) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

function setLibraryAccessCookie() {
  if (typeof document === 'undefined') return;
  document.cookie = `${LIBRARY_ACCESS_COOKIE}=1; max-age=${30 * 86400}; path=/; SameSite=Lax; Secure`;
}

export function isTokenExpired(token: string | null): boolean {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (payload && typeof payload.exp === 'number') {
      return Date.now() / 1000 >= payload.exp - 5;
    }
    return false;
  } catch {
    return true;
  }
}

export const authStorage = {
  getToken(): string | null {
    const token = localStorage.getItem(AUTH_TOKEN_KEY) || getCookie(AUTH_TOKEN_KEY);

    if (!token || isTokenExpired(token)) {
      if (token || localStorage.getItem(AUTH_USER_KEY) || localStorage.getItem(SUBSCRIPTION_ACTIVE_KEY) || getCookie(AUTH_TOKEN_KEY)) {
        this.clearToken();
      }
      return null;
    }

    if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
    }
    if (!getCookie(AUTH_TOKEN_KEY)) {
      setCookie(AUTH_TOKEN_KEY, token);
    }

    return token;
  },
  getUser<T = unknown>() {
    const rawUser = localStorage.getItem(AUTH_USER_KEY);

    if (!rawUser) return null;

    try {
      return JSON.parse(rawUser) as T;
    } catch {
      localStorage.removeItem(AUTH_USER_KEY);
      return null;
    }
  },
  hasToken(): boolean {
    return Boolean(this.getToken());
  },
  hasActiveSubscription() {
    if (!this.hasToken()) return false;
    return localStorage.getItem(SUBSCRIPTION_ACTIVE_KEY) === 'true'
      || hasUserSubscriptionAccess(this.getUser<AuthUser>());
  },
  setToken(token: string) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    setCookie(AUTH_TOKEN_KEY, token);
    localStorage.setItem(SUBSCRIPTION_ACTIVE_KEY, 'false');
    removeCookie(LIBRARY_ACCESS_COOKIE);
    dispatchAuthChange();
    dispatchSubscriptionChange();
  },
  setUser(user: unknown) {
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    dispatchAuthChange();
  },
  setSubscriptionActive(isActive: boolean) {
    localStorage.setItem(SUBSCRIPTION_ACTIVE_KEY, String(isActive));
    if (isActive) {
      setLibraryAccessCookie();
    } else {
      removeCookie(LIBRARY_ACCESS_COOKIE);
    }
    dispatchSubscriptionChange();
  },
  clearToken() {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(SUBSCRIPTION_ACTIVE_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    removeCookie(AUTH_TOKEN_KEY);
    removeCookie(LIBRARY_ACCESS_COOKIE);
    dispatchAuthChange();
    dispatchSubscriptionChange();
  },
};
